import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { MapPin, Plus, Trash2, LocateFixed, Users, Clock, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { value: "main_lab", label: "Main Lab" },
  { value: "collection_centre", label: "Collection Centre" },
  { value: "satellite_lab", label: "Satellite Lab" },
];

const emptyForm = { name: "", type: "collection_centre", address: "", latitude: "", longitude: "", radius_m: 200 };

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];   // index = Python weekday()

// Left / Terminated / Retired staff past their leaving date can't be given a location, shift or deputation.
const hasExited = (e) => ["Left", "Terminated", "Retired"].includes(e.employment?.employment_status)
  && (!e.employment?.date_of_leaving || e.employment.date_of_leaving < new Date().toISOString().slice(0, 10));
const attendanceTracked = (e) => !e.top_management && !e.exempt_from_rules && !hasExited(e)
  && !["Contract", "Consultant"].includes(e.employment?.employee_type);

export default function AttendanceLocations() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [assignEmp, setAssignEmp] = useState("");
  const [assignLoc, setAssignLoc] = useState("");
  const [shiftEmp, setShiftEmp] = useState("");
  const [shiftStart, setShiftStart] = useState("09:00");
  const [shiftHours, setShiftHours] = useState(8);
  const [saturdayHalfDay, setSaturdayHalfDay] = useState(false);
  const [flexDays, setFlexDays] = useState([]);
  const [flexDates, setFlexDates] = useState([]);
  const [flexDateInput, setFlexDateInput] = useState("");
  const [depEmp, setDepEmp] = useState("");
  const [depLoc, setDepLoc] = useState("");
  const [depStart, setDepStart] = useState("");
  const [depEnd, setDepEnd] = useState("");
  const [depReason, setDepReason] = useState("");

  const { data: locations, isLoading } = useQuery({ queryKey: ["attLocations"], queryFn: async () => (await api.get("/attendance/locations")).data });
  const { data: assignments } = useQuery({ queryKey: ["attAssignments"], queryFn: async () => (await api.get("/attendance/assignments")).data });
  const { data: employees } = useQuery({ queryKey: ["employeesLite"], queryFn: async () => (await api.get("/employees")).data });
  const { data: shifts } = useQuery({ queryKey: ["attShifts"], queryFn: async () => (await api.get("/attendance/shifts")).data });
  const { data: deputations } = useQuery({ queryKey: ["attDeputations"], queryFn: async () => (await api.get("/attendance/deputations")).data });

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available in this browser."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })),
      () => toast.error("Couldn't get your current location."),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const create = useMutation({
    mutationFn: async () => (await api.post("/attendance/locations", {
      ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), radius_m: Number(form.radius_m),
    })).data,
    onSuccess: () => { toast.success("Location added"); qc.invalidateQueries({ queryKey: ["attLocations"] }); setForm(emptyForm); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const archive = useMutation({
    mutationFn: async (id) => (await api.delete(`/attendance/locations/${id}`)).data,
    onSuccess: () => { toast.success("Location archived"); qc.invalidateQueries({ queryKey: ["attLocations"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const assign = useMutation({
    mutationFn: async () => (await api.put(`/attendance/employees/${assignEmp}/location`, { location_id: assignLoc })).data,
    onSuccess: (d) => { toast.success(`Assigned to ${d.location_name}`); qc.invalidateQueries({ queryKey: ["attAssignments"] }); setAssignEmp(""); setAssignLoc(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const assignShift = useMutation({
    mutationFn: async () => (await api.put(`/attendance/employees/${shiftEmp}/shift`, {
      shift_start: shiftStart, shift_hours: Number(shiftHours), saturday_half_day: saturdayHalfDay,
      // Only a Super Admin sends flexible days; for HR they're left out so the server keeps them as they are.
      ...(isSuperAdmin ? { flexible_days: flexDays, flexible_dates: flexDates } : {}),
    })).data,
    onSuccess: () => {
      toast.success("Shift assigned"); qc.invalidateQueries({ queryKey: ["attShifts"] });
      setShiftEmp(""); setSaturdayHalfDay(false); setFlexDays([]); setFlexDates([]); setFlexDateInput("");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const createDeputation = useMutation({
    mutationFn: async () => (await api.post("/attendance/deputations", {
      employee_id: depEmp, location_id: depLoc, start_date: depStart, end_date: depEnd, reason: depReason,
    })).data,
    onSuccess: (d) => {
      toast.success(`${d.employee_name} can now check in at ${d.location_name} through ${d.end_date}`);
      qc.invalidateQueries({ queryKey: ["attDeputations"] });
      setDepEmp(""); setDepLoc(""); setDepStart(""); setDepEnd(""); setDepReason("");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const cancelDeputation = useMutation({
    mutationFn: async (id) => (await api.delete(`/attendance/deputations/${id}`)).data,
    onSuccess: () => { toast.success("Deputation cancelled"); qc.invalidateQueries({ queryKey: ["attDeputations"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const canSubmit = form.name && form.latitude !== "" && form.longitude !== "" && !create.isPending;
  const active = (locations || []).filter((l) => l.status === "Active");

  return (
    <div>
      <PageHeader overline="Attendance" title="Locations" icon={MapPin}
        subtitle="Recognized IntenCiv sites for geofenced check-in — main lab, collection centres, satellite labs." />

      <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-6 gap-3 items-end mb-4">
        <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Collection Centre - Vaishali Nagar" data-testid="loc-name" /></div>
        <div>
          <Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Latitude</Label><Input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="mt-1" placeholder="26.9124" data-testid="loc-lat" /></div>
        <div><Label>Longitude</Label><Input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="mt-1" placeholder="75.7873" data-testid="loc-lon" /></div>
        <div><Label>Radius (m)</Label><Input type="number" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} className="mt-1" /></div>
        <div className="sm:col-span-4"><Label>Address (optional)</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
        <Button type="button" variant="outline" onClick={useMyLocation}><LocateFixed className="h-4 w-4 mr-2" /> Use my current location</Button>
        <Button className="bg-navy hover:bg-navy-700" disabled={!canSubmit} onClick={() => create.mutate()} data-testid="add-location"><Plus className="h-4 w-4 mr-1" /> Add Location</Button>
      </Card>

      {isLoading ? <Loading /> : active.length === 0 ? (
        <EmptyState icon={MapPin} title="No locations yet" hint="Add your main lab, collection centres, and satellite labs above." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden mb-6">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Name</TableHead>
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="overline text-slate-500">Coordinates</TableHead>
              <TableHead className="overline text-slate-500">Radius</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {active.map((l) => (
                <TableRow key={l.id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{l.name}</TableCell>
                  <TableCell className="text-slate-600">{TYPES.find((t) => t.value === l.type)?.label || l.type}</TableCell>
                  <TableCell className="font-mono-i text-sm text-teal">{l.latitude}, {l.longitude}</TableCell>
                  <TableCell className="text-slate-600">{l.radius_m} m</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-red-500" onClick={() => archive.mutate(l.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PageHeader overline="Attendance" title="Employee Assignments" icon={Users}
        subtitle="Set each employee's permanent check-in location." />
      <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-3 gap-3 items-end mb-4">
        <div>
          <Label>Employee</Label>
          <Select value={assignEmp} onValueChange={setAssignEmp}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent className="bg-white">{(employees || []).filter(attendanceTracked).map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({e.employee_id})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Location</Label>
          <Select value={assignLoc} onValueChange={setAssignLoc}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent className="bg-white">{active.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button className="bg-navy hover:bg-navy-700" disabled={!assignEmp || !assignLoc || assign.isPending} onClick={() => assign.mutate()}>Assign</Button>
      </Card>

      {(assignments || []).length > 0 && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Location</TableHead>
              <TableHead className="overline text-slate-500">Assigned</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {assignments.map((a) => (
                <TableRow key={a.id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{a.employee_name} <span className="text-slate-400 font-mono-i text-xs">({a.employee_code})</span></TableCell>
                  <TableCell className="text-slate-600">{a.location_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{new Date(a.assigned_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PageHeader overline="Attendance" title="Shift Assignment" icon={Clock}
        subtitle="Set each employee's scheduled check-in time and shift duration — used to compute late/early grace and half-day marking. Turn on Saturday half day for employees who only need to work 4 hours on Saturdays. A Super Admin can mark Flexible days (weekly or one-off) where only hours worked count." />
      <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-5 gap-3 items-end mb-4">
        <div>
          <Label>Employee</Label>
          <Select value={shiftEmp} onValueChange={(v) => {
            setShiftEmp(v);
            const existing = (shifts || []).find((s) => s.employee_id === v);
            if (existing) { setShiftStart(existing.shift_start); setShiftHours(existing.shift_hours); }
            setSaturdayHalfDay(!!existing?.saturday_half_day);
            setFlexDays(existing?.flexible_days || []);
            setFlexDates(existing?.flexible_dates || []);
          }}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent className="bg-white">{(employees || []).filter(attendanceTracked).map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({e.employee_id})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Shift start</Label><Input type="time" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} className="mt-1" /></div>
        <div><Label>Shift hours</Label><Input type="number" step="0.5" min="1" max="16" value={shiftHours} onChange={(e) => setShiftHours(e.target.value)} className="mt-1" /></div>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-600">
          <Switch checked={saturdayHalfDay} onCheckedChange={setSaturdayHalfDay} data-testid="shift-saturday-half-day" /> Saturday half day (4 hrs)
        </label>
        <Button className="bg-navy hover:bg-navy-700" disabled={!shiftEmp || !shiftStart || !shiftHours || assignShift.isPending} onClick={() => assignShift.mutate()}>Assign Shift</Button>
        <div className="sm:col-span-5 border-t border-slate-100 pt-3" data-testid="flexible-days">
          <Label>Flexible days {!isSuperAdmin && <span className="text-slate-400 font-normal">(set by Super Admin only)</span>}</Label>
          <p className="text-xs text-slate-500 mt-0.5">No late/early marking on these days — 4+ hours worked counts as a full day, under 4 hours is Half Day.</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {WEEKDAYS.map((label, i) => {
              const on = flexDays.includes(i);
              return (
                <button key={label} type="button" disabled={!isSuperAdmin}
                  onClick={() => setFlexDays(on ? flexDays.filter((d) => d !== i) : [...flexDays, i].sort())}
                  className={`px-3 py-1 rounded-full text-sm border ${on ? "bg-teal text-white border-teal" : "bg-white text-slate-600 border-slate-200"} ${!isSuperAdmin ? "opacity-60 cursor-not-allowed" : ""}`}>
                  {label}
                </button>
              );
            })}
            <span className="text-slate-300 mx-1">|</span>
            <Input type="date" value={flexDateInput} onChange={(e) => setFlexDateInput(e.target.value)} disabled={!isSuperAdmin} className="w-40 h-8" />
            <Button type="button" size="sm" variant="outline" disabled={!isSuperAdmin || !flexDateInput}
              onClick={() => { if (!flexDates.includes(flexDateInput)) setFlexDates([...flexDates, flexDateInput].sort()); setFlexDateInput(""); }}>
              <Plus className="h-3 w-3 mr-1" /> One-off date
            </Button>
            {flexDates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-mono-i">
                {d}
                {isSuperAdmin && <button type="button" className="text-red-500" onClick={() => setFlexDates(flexDates.filter((x) => x !== d))}>×</button>}
              </span>
            ))}
          </div>
        </div>
      </Card>

      {(shifts || []).length > 0 && (
        <Card className="border-slate-200 shadow-none overflow-hidden mb-6">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Shift Start</TableHead>
              <TableHead className="overline text-slate-500">Shift Hours</TableHead>
              <TableHead className="overline text-slate-500">Saturday</TableHead>
              <TableHead className="overline text-slate-500">Flexible Days</TableHead>
              <TableHead className="overline text-slate-500">Assigned</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shifts.map((s) => (
                <TableRow key={s.id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{s.employee_name} <span className="text-slate-400 font-mono-i text-xs">({s.employee_code})</span></TableCell>
                  <TableCell className="font-mono-i text-sm text-teal">{s.shift_start}</TableCell>
                  <TableCell className="text-slate-600">{s.shift_hours}h</TableCell>
                  <TableCell className="text-slate-600">{s.saturday_half_day ? "Half day (4h)" : "Full shift"}</TableCell>
                  <TableCell className="text-slate-600 text-sm">
                    {[...(s.flexible_days || []).map((d) => WEEKDAYS[d]), ...(s.flexible_dates || [])].join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{new Date(s.assigned_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PageHeader overline="Attendance" title="Deputation" icon={Shuffle}
        subtitle="Temporarily allow an employee to check in at a different, already-recognized IntenCiv location — e.g. covering for someone on leave — for a date range." />
      <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-6 gap-3 items-end mb-4">
        <div className="sm:col-span-2">
          <Label>Employee</Label>
          <Select value={depEmp} onValueChange={setDepEmp}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent className="bg-white">{(employees || []).filter(attendanceTracked).map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({e.employee_id})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Deputed to Location</Label>
          <Select value={depLoc} onValueChange={setDepLoc}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent className="bg-white">{active.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>From</Label><Input type="date" value={depStart} onChange={(e) => setDepStart(e.target.value)} className="mt-1" /></div>
        <div><Label>To</Label><Input type="date" value={depEnd} onChange={(e) => setDepEnd(e.target.value)} className="mt-1" /></div>
        <div className="sm:col-span-5"><Label>Reason</Label><Input value={depReason} onChange={(e) => setDepReason(e.target.value)} className="mt-1" placeholder="e.g. Covering for Priya S. (on leave) at Vaishali Nagar" /></div>
        <Button className="bg-navy hover:bg-navy-700" disabled={!depEmp || !depLoc || !depStart || !depEnd || depReason.length < 3 || createDeputation.isPending} onClick={() => createDeputation.mutate()}>Create Deputation</Button>
      </Card>

      {(deputations || []).length > 0 && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Deputed to</TableHead>
              <TableHead className="overline text-slate-500">From → To</TableHead>
              <TableHead className="overline text-slate-500">Reason</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {deputations.map((d) => (
                <TableRow key={d.id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{d.employee_name} <span className="text-slate-400 font-mono-i text-xs">({d.employee_code})</span></TableCell>
                  <TableCell className="text-slate-600">{d.location_name}</TableCell>
                  <TableCell className="text-slate-600">{d.start_date} → {d.end_date}</TableCell>
                  <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{d.reason}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancelDeputation.mutate(d.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
